import { Separator } from '@/components/ui/separator'
import {
  createFileRoute,
  useNavigate,
  useRouteContext,
} from '@tanstack/react-router'
import { useState, useCallback, useTransition, useMemo, Suspense } from 'react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { FilterIcon, LayoutGridIcon, ListIcon } from 'lucide-react'
import { DraftPublishTabs } from '@/components/dashboard/DraftPublishTabs'
import { useUrlSearchParamsState } from '@/hooks/useUrlSearchParamsState'
import { GridView } from '@/components/dashboard/documents-page/DocumentsPageContent'
import {
  useDocumentTableColumns,
  DocumentsTable,
} from '@/components/dashboard/documents-page/DocumentsTable'
import DocumentsSidebar from '@/components/dashboard/documents-page/DocumentsSidebar'
import type {
  ColumnFilter,
  SortingState,
  OnChangeFn,
  PaginationState,
} from '@tanstack/react-table'
import {
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table'
import type {
  Project,
  UserInfo,
  DocumentData,
  WorkspaceAccess,
} from '@/data/entities'
import {
  canEditProject,
  filterDocumentsByAccess,
  getProjectRoles,
  getVisibleProjects,
  getWorkspaceAccess,
} from '@/data/entities'
import ErrorDocumentPage from '@/components/dashboard/documents-page/ErrorDocumentPage'
import { LoadingAllDocumentsContent } from '@/components/loading-ui/LoadingAllDocumentsContent'
import { useSuspenseQuery } from '@tanstack/react-query'
import { Input } from '@/components/ui/input'
import { useLocalStorageState } from '@/hooks/useLocalStorageState'
import { MultiSelect } from '@/components/ui/multi-select'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'
import {
  allDocumentsOptions,
  usersOptions,
  tenantProjectsOptions,
  orgPermissionsCurrentUserOptions,
} from '@/data/queryOptions'
import { REFRESH_INTERVAL } from '@/data/constants'
import { Button } from '@/components/ui/button'
import { useIsMobile } from '@/hooks/use-mobile'
import {
  Dialog,
  DialogTitle,
  DialogHeader,
  DialogContent,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { ExportCSVDialog } from '@/components/dashboard/documents-page/ExportCSVDialog'
import { DashboardLayout } from '@/routes/_authenticated/_dashboard'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DraftModeBanner } from '@/components/document-edit/DraftModeBanner'
import { z } from 'zod'

type DocumentsRouteSearch = {
  stage?: 'all' | 'published' | 'draft'
}

const DocumentsRouteSearchSchema = z.object({
  stage: z.enum(['all', 'published', 'draft']).optional(),
})

export const Route = createFileRoute('/_authenticated/org/$orgId/documents/')({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: 'All Company Documents - LineWise',
      },
    ],
  }),
  errorComponent: ({ error }) => <ErrorDocumentPage error={error} />,
  validateSearch: (search: DocumentsRouteSearch) => {
    return DocumentsRouteSearchSchema.parse(search)
  },
})

function RouteComponent() {
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()
  const isMobile = useIsMobile()
  const { hasAnyEditorAccess, isOrgAdmin, isOrgEditor, permissionExpression } =
    useRouteContext({
      from: '/_authenticated',
    })
  const navigate = useNavigate()
  const canEdit = hasAnyEditorAccess
  const [stage, setStage] = useUrlSearchParamsState<
    'all' | 'published' | 'draft'
  >({
    name: 'stage',
    defaultValue: canEdit ? 'all' : 'published',
  })

  const { data: allUsers } = useSuspenseQuery(
    usersOptions(TENANT_ID!, idToken!),
  )

  const { data: projects } = useSuspenseQuery(
    tenantProjectsOptions(TENANT_ID!, idToken!),
  )
  const { data: orgPermissions } = useSuspenseQuery(
    orgPermissionsCurrentUserOptions(TENANT_ID!, idToken!),
  )
  const workspaceAccess = useMemo(
    () => getWorkspaceAccess(orgPermissions, TENANT_ID!),
    [orgPermissions, TENANT_ID],
  )
  const visibleProjects = useMemo(
    () =>
      getVisibleProjects(projects ?? [], {
        isOrgAdmin: !!isOrgAdmin,
        isOrgEditor: !!isOrgEditor,
        workspaceAccess,
      }),
    [projects, workspaceAccess, isOrgAdmin, isOrgEditor],
  )
  const projectRoles = useMemo(
    () => getProjectRoles(permissionExpression ?? undefined, TENANT_ID!),
    [permissionExpression, TENANT_ID],
  )
  const editableProjectIds = useMemo(
    () =>
      new Set<string>(
        visibleProjects
          .filter((project) =>
            canEditProject({
              isOrgAdmin: !!isOrgAdmin,
              isOrgEditor: !!isOrgEditor,
              projectRole: projectRoles.get(project.id),
            }),
          )
          .map((project) => project.id),
      ),
    [visibleProjects, isOrgAdmin, isOrgEditor, projectRoles],
  )
  const [viewType, setViewType] = useLocalStorageState<
    'grid' | 'list' | 'table'
  >('documents-view-type', 'grid')
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'createdAt',
      desc: true,
    },
  ])
  const [globalFilterInputText, setGlobalFilterInputText] = useState('')
  const [typeFilter, setTypeFilter] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [creatorFilter, setcreatorFilter] = useState<string[]>([])
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [_isPending, startTransition] = useTransition()

  const handleFilterTextChange = useCallback((value: string) => {
    startTransition(() => {
      setGlobalFilterInputText(value)
    })
  }, [])

  if (!canEdit && stage !== 'published') {
    console.log('redirecting to published', stage)
    navigate({
      to: '/org/$orgId/documents',
      params: { orgId: TENANT_ID! },
      search: {},
      replace: true,
    })
  }

  return (
    <DashboardLayout>
      <main
        className={cn('main-container', isMobile && 'mt-15 mb-20 flex-col')}
      >
        <DocumentsSidebar projects={visibleProjects} />
        <div className="dashboard-page-container">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl">All Company Documents</h1>
            <Tabs
              value={viewType}
              onValueChange={(value) =>
                setViewType(value as 'grid' | 'list' | 'table')
              }
              tabIndex={-1}
            >
              <TabsList tabIndex={-1}>
                <TabsTrigger value="grid" tabIndex={-1}>
                  <LayoutGridIcon />
                </TabsTrigger>
                <TabsTrigger value="list" tabIndex={-1}>
                  <ListIcon />
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          {canEdit && (
            <div className="flex items-center justify-between gap-4">
              <DraftPublishTabs stage={stage} onStageChange={setStage} />
              <ExportCSVDialog
                stage={stage}
                typeFilter={typeFilter}
                creatorFilter={creatorFilter}
                allUsers={allUsers}
                projects={visibleProjects}
              />
            </div>
          )}
          <Suspense fallback={<LoadingAllDocumentsContent />}>
            {!canEdit ? (
              <AllPublishedDocumentsWrapper
                viewType={viewType}
                sorting={sorting}
                setSorting={setSorting}
                globalFilterInputText={globalFilterInputText}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                creatorFilter={creatorFilter}
                setcreatorFilter={setcreatorFilter}
                handleFilterTextChange={handleFilterTextChange}
                allUsers={allUsers}
                projects={visibleProjects}
                workspaceAccess={workspaceAccess}
                isOrgAdmin={!!isOrgAdmin}
                isOrgEditor={!!isOrgEditor}
                editableProjectIds={editableProjectIds}
                isMobile={isMobile}
                pagination={pagination}
                setPagination={setPagination}
              />
            ) : stage === 'published' ? (
              <AllPublishedDocumentsWrapper
                viewType={viewType}
                sorting={sorting}
                setSorting={setSorting}
                globalFilterInputText={globalFilterInputText}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                creatorFilter={creatorFilter}
                setcreatorFilter={setcreatorFilter}
                handleFilterTextChange={handleFilterTextChange}
                allUsers={allUsers}
                projects={visibleProjects}
                workspaceAccess={workspaceAccess}
                isOrgAdmin={!!isOrgAdmin}
                isOrgEditor={!!isOrgEditor}
                editableProjectIds={editableProjectIds}
                isMobile={isMobile}
                pagination={pagination}
                setPagination={setPagination}
              />
            ) : stage === 'draft' ? (
              <AllDraftDocumentsWrapper
                viewType={viewType}
                sorting={sorting}
                setSorting={setSorting}
                globalFilterInputText={globalFilterInputText}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                creatorFilter={creatorFilter}
                setcreatorFilter={setcreatorFilter}
                handleFilterTextChange={handleFilterTextChange}
                allUsers={allUsers}
                projects={visibleProjects}
                workspaceAccess={workspaceAccess}
                isOrgAdmin={!!isOrgAdmin}
                isOrgEditor={!!isOrgEditor}
                editableProjectIds={editableProjectIds}
                isMobile={isMobile}
                pagination={pagination}
                setPagination={setPagination}
              />
            ) : (
              <AllDocumentsListWrapper
                viewType={viewType}
                sorting={sorting}
                setSorting={setSorting}
                globalFilterInputText={globalFilterInputText}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                creatorFilter={creatorFilter}
                setcreatorFilter={setcreatorFilter}
                handleFilterTextChange={handleFilterTextChange}
                allUsers={allUsers}
                projects={visibleProjects}
                workspaceAccess={workspaceAccess}
                isOrgAdmin={!!isOrgAdmin}
                isOrgEditor={!!isOrgEditor}
                editableProjectIds={editableProjectIds}
                isMobile={isMobile}
                pagination={pagination}
                setPagination={setPagination}
              />
            )}
          </Suspense>
        </div>
      </main>
    </DashboardLayout>
  )
}

// Display component - receives documents as props
type DocumentsDisplayProps = {
  documents: DocumentData[]
  totalItems: number
  viewType: 'grid' | 'list' | 'table'
  sorting: SortingState
  setSorting: OnChangeFn<SortingState>
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  globalFilterInputText: string
  typeFilter: string[]
  setTypeFilter: (value: string[]) => void
  statusFilter: string[]
  setStatusFilter: (value: string[]) => void
  creatorFilter: string[]
  setcreatorFilter: (value: string[]) => void
  handleFilterTextChange: (value: string) => void
  allUsers: UserInfo[]
  projects: Project[] | null | undefined
  isMobile: boolean
  showDraftBanner?: boolean
}

function DocumentsDisplay({
  documents,
  totalItems,
  viewType,
  sorting,
  setSorting,
  pagination,
  setPagination,
  globalFilterInputText,
  typeFilter,
  setTypeFilter,
  statusFilter,
  setStatusFilter,
  creatorFilter,
  setcreatorFilter,
  handleFilterTextChange,
  allUsers,
  projects,
  isMobile,
  showDraftBanner = false,
}: DocumentsDisplayProps) {
  const typeFiltersList = useMemo(() => {
    return [...new Set(documents.map((doc) => doc.metadata?.type))].filter(
      (type) => type !== undefined,
    ) as string[]
  }, [documents])

  const statusFiltersList = useMemo(() => {
    return [...new Set(documents.map((doc) => doc.status))].filter(
      (status) => status !== undefined,
    )
  }, [documents])

  const columns = useDocumentTableColumns(documents)

  const columnFilters = useMemo(() => {
    return [
      typeFilter.length > 0 ? { id: 'type', value: typeFilter } : undefined,
      statusFilter.length > 0
        ? { id: 'status', value: statusFilter }
        : undefined,
      creatorFilter.length > 0
        ? { id: 'creator', value: creatorFilter }
        : undefined,
    ].filter(Boolean) as ColumnFilter[]
  }, [typeFilter, statusFilter, creatorFilter])

  const pageCount = Math.ceil(totalItems / pagination.pageSize) || 1

  const table = useReactTable({
    data: documents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    manualPagination: true,
    pageCount,
    state: {
      sorting,
      globalFilter: globalFilterInputText,
      pagination,
      columnFilters,
    },
    columnResizeMode: 'onChange',
    defaultColumn: {
      size: 80,
      minSize: 80,
    },
  })

  return (
    <>
      <div className="flex flex-row flex-wrap items-center gap-4">
        <Input
          placeholder="Search documents"
          className="flex-1 md:min-h-10 md:min-w-[220px]"
          onChange={(e) => handleFilterTextChange(e.target.value)}
        />
        {isMobile && (
          <MobileFilters
            filters={{
              'Document Type': {
                options: typeFiltersList.map((type) => ({
                  label: type,
                  value: type,
                })),
                onFilterChange: (value) => setTypeFilter(value),
              },
              Status: {
                options: statusFiltersList.map((status) => ({
                  label: status,
                  value: status,
                })),
                onFilterChange: (value) => setStatusFilter(value),
              },
              Author: {
                options: allUsers.map((user) => ({
                  label: user.name ?? 'Unknown',
                  value: user.uid ?? 'Unknown',
                })),
                onFilterChange: (value) => setcreatorFilter(value),
              },
            }}
          />
        )}
        {!isMobile && (
          <>
            <MultiSelect
              options={typeFiltersList.map((type) => ({
                label: type,
                value: type,
              }))}
              onValueChange={(value) => setTypeFilter(value)}
              className="w-full sm:w-[220px]"
              placeholder="Document Type"
              maxCount={1}
            />
            <MultiSelect
              options={statusFiltersList.map((status) => ({
                label: status,
                value: status,
              }))}
              onValueChange={(value) => setStatusFilter(value)}
              className="w-full sm:w-[220px]"
              placeholder="Status"
              maxCount={1}
            />
            <MultiSelect
              options={allUsers.map((user) => ({
                label: user.name ?? 'Unknown',
                value: user.uid ?? 'Unknown',
              }))}
              onValueChange={(value) => setcreatorFilter(value)}
              className="w-full sm:w-[220px]"
              placeholder="Author"
              maxCount={1}
            />
          </>
        )}
      </div>
      <Separator />
      {showDraftBanner && (
        <DraftModeBanner
          isViewingDraft={true}
          customMessage="The content in this tab is only visible to editors and administrators."
          className="rounded-full"
        />
      )}
      {viewType === 'grid' ? (
        <GridView
          documents={table.getRowModel().rows.map((row) => row.original)}
          className="min-h-[60vh] grow"
          projects={projects ?? []}
          currentProjectId={null}
        />
      ) : (
        <DocumentsTable table={table} className="min-h-[50vh] grow" />
      )}
      {/* Pagination controls */}
      <div className="flex items-center justify-between px-2 py-3">
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <span>{totalItems} items</span>
          <Select
            value={String(pagination.pageSize)}
            onValueChange={(v) => {
              table.setPageSize(Number(v))
              table.setPageIndex(0)
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 20, 50, 100].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span>per page</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <span className="text-sm">
            Page {pagination.pageIndex + 1} of {table.getPageCount() || 1}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </>
  )
}

// Shared wrapper props type
type AllDocumentsWrapperProps = {
  viewType: 'grid' | 'list' | 'table'
  sorting: SortingState
  setSorting: OnChangeFn<SortingState>
  globalFilterInputText: string
  typeFilter: string[]
  setTypeFilter: (value: string[]) => void
  statusFilter: string[]
  setStatusFilter: (value: string[]) => void
  creatorFilter: string[]
  setcreatorFilter: (value: string[]) => void
  handleFilterTextChange: (value: string) => void
  allUsers: UserInfo[]
  projects: Project[] | null | undefined
  isMobile: boolean
  workspaceAccess: WorkspaceAccess
  isOrgAdmin: boolean
  isOrgEditor: boolean
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  editableProjectIds: Set<string>
}

// Wrapper for published documents only
function AllPublishedDocumentsWrapper(props: AllDocumentsWrapperProps) {
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()
  const { pagination } = props

  const { data: result } = useSuspenseQuery({
    ...allDocumentsOptions({
      tenantId: TENANT_ID!,
      token: idToken!,
      stage: 'published',
      offset: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
    }),
    refetchInterval: REFRESH_INTERVAL,
  })

  const filteredDocuments = useMemo(
    () =>
      filterDocumentsByAccess(result?.data ?? [], props.workspaceAccess, {
        isOrgAdmin: props.isOrgAdmin,
        isOrgEditor: props.isOrgEditor,
      }),
    [result, props.workspaceAccess, props.isOrgAdmin, props.isOrgEditor],
  )

  return (
    <DocumentsDisplay
      documents={filteredDocuments}
      totalItems={result?.total ?? 0}
      {...props}
    />
  )
}

// Wrapper for draft documents (draft + pending_changes)
function AllDraftDocumentsWrapper(props: AllDocumentsWrapperProps) {
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()
  const { pagination } = props

  const { data: draftResult } = useSuspenseQuery({
    ...allDocumentsOptions({
      tenantId: TENANT_ID!,
      token: idToken!,
      stage: 'draft',
      offset: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
    }),
    refetchInterval: REFRESH_INTERVAL,
  })

  const { data: pendingResult } = useSuspenseQuery({
    ...allDocumentsOptions({
      tenantId: TENANT_ID!,
      token: idToken!,
      stage: 'pending_changes',
      offset: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
    }),
    refetchInterval: REFRESH_INTERVAL,
  })

  // Combine draft and pending_changes, dedupe by id
  const draftAndPendingChangesSops = useMemo(() => {
    const combined = [
      ...(draftResult?.data || []),
      ...(pendingResult?.data || []),
    ]
    const seen = new Set<string>()
    return combined.filter((doc) => {
      if (seen.has(doc.id)) return false
      seen.add(doc.id)
      return true
    })
  }, [draftResult, pendingResult])

  const totalItems = (draftResult?.total ?? 0) + (pendingResult?.total ?? 0)

  return (
    <DocumentsDisplay
      documents={draftAndPendingChangesSops.filter((document) =>
        props.editableProjectIds.has(document.projectId),
      )}
      totalItems={totalItems}
      showDraftBanner={true}
      {...props}
    />
  )
}

// Wrapper for all documents
function AllDocumentsListWrapper(props: AllDocumentsWrapperProps) {
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()
  const { pagination } = props

  const { data: result } = useSuspenseQuery({
    ...allDocumentsOptions({
      tenantId: TENANT_ID!,
      token: idToken!,
      stage: undefined,
      offset: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
    }),
    refetchInterval: REFRESH_INTERVAL,
  })

  const filteredDocuments = useMemo(
    () =>
      filterDocumentsByAccess(result?.data ?? [], props.workspaceAccess, {
        isOrgAdmin: props.isOrgAdmin,
        isOrgEditor: props.isOrgEditor,
      }),
    [result, props.workspaceAccess, props.isOrgAdmin, props.isOrgEditor],
  )

  return (
    <DocumentsDisplay
      documents={filteredDocuments}
      totalItems={result?.total ?? 0}
      {...props}
    />
  )
}

type Option = {
  label: string
  value: string
}

type Filter = {
  options: Option[]
  onFilterChange: (value: string[]) => void
}

type MobileFiltersProps = {
  filters: Record<string, Filter>
}

function MobileFilters({ filters }: MobileFiltersProps) {
  const [selected, setSelected] = useState<
    Record<string, Record<string, boolean>>
  >({})
  const [open, setOpen] = useState(false)

  const handleApply = useCallback(() => {
    Object.entries(selected).forEach(([key, value]) => {
      filters[key].onFilterChange(
        Object.entries(value)
          .filter(([_, value]) => value)
          .map(([key]) => key),
      )
    })
    setOpen(false)
  }, [selected, filters])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <FilterIcon />
          Filters
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[90vh] flex-col">
        <DialogHeader>
          <DialogTitle>Filters</DialogTitle>
        </DialogHeader>
        <div className="flex flex-1 flex-col gap-2 overflow-y-scroll">
          <Accordion type="multiple">
            {Object.entries(filters).map(([key, filter]) => (
              <AccordionItem value={key} key={key}>
                <AccordionTrigger>{key}</AccordionTrigger>
                <AccordionContent className="flex flex-col gap-2">
                  {filter.options.map((option) => (
                    <Label htmlFor={option.value} key={option.value}>
                      <div className="flex h-8 flex-row items-center gap-2">
                        <Checkbox
                          id={option.value}
                          checked={selected[key]?.[option.value] ?? false}
                          onCheckedChange={(checked) => {
                            setSelected((prev) => ({
                              ...prev,
                              [key]: {
                                ...prev[key],
                                [option.value]: checked === true,
                              },
                            }))
                          }}
                        />
                        {option.label}
                      </div>
                    </Label>
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        <div className="flex flex-row gap-2">
          <Button
            className="flex-1"
            variant="outline"
            onClick={() => setSelected({})}
          >
            Clear
          </Button>
          <Button className="flex-1" variant="default" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
